/*
 * Copyright (c) 2024 OCLC, Inc. All Rights Reserved.
 *
 * OCLC proprietary information: the enclosed materials contain
 * proprietary information of OCLC, Inc. and shall not be disclosed in whole or in
 * any part to any third party or used by any person for any purpose, without written
 * consent of OCLC, Inc.  Duplication of any portion of these  materials shall include this notice.
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid2 as Grid, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'lodash';

import messages from '../../utils/messages';
import logo from '../../images/logo.png';

/**
 * Header component that displays the institution name and logo based on the login.
 */
const BrandedHeader = React.memo(({ branding, userDisplayName }) => {
  const { formatMessage } = useIntl();
  const { displayName, logo } = branding;

  const isValidLogo = logo?.url && logo.url.startsWith('https://');

  const altText =
    logo?.altText ||
    formatMessage(messages.logoAltText, { institutionName: displayName });

  return (
    <Grid container direction='column' spacing={4}>
      <Box
        data-testid='display-name'
        padding='8px 8px 0px 8px'
        display='flex'
        justifyContent='space-between'
      >
        <Typography variant='labelSm'>{displayName}</Typography>
        {userDisplayName && (
          <Typography variant='labelSm' sx={{ textAlign: 'right' }}>
            {userDisplayName}
          </Typography>
        )}
      </Box>
      {isValidLogo && (
        <Grid
          data-testid='logo-banner'
          p={4}
          sx={{
            bgcolor: `#${logo.backgroundColor}`,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <img
            data-testid='header-logo'
            src={logo.url}
            alt={altText}
            title={altText}
            width='auto'
            height='86px'
          />
        </Grid>
      )}
    </Grid>
  );
});

BrandedHeader.propTypes = {
  branding: PropTypes.object.isRequired,
  userDisplayName: PropTypes.string,
};

const NormalHeader = React.memo(({ userDisplayName }) => {
  return (
    <Grid
      container
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        height: '82px',
      }}
    >
      {userDisplayName && (
        <Box
          data-testid='display-name'
          p={4}
          display='flex'
          justifyContent='end'
        >
          <Typography variant='labelSm' sx={{ textAlign: 'right' }}>
            {userDisplayName}
          </Typography>
        </Box>
      )}
      <FormattedMessage id='Logo.altText' defaultMessage='OCLC logo'>
        {(altText) => (
          <img
            data-testid='header-logo'
            src={logo}
            alt={altText}
            title={altText}
            width='150px'
            height='50px'
          />
        )}
      </FormattedMessage>
    </Grid>
  );
});

NormalHeader.propTypes = {
  userDisplayName: PropTypes.string,
};

const Header = React.memo(({ branding, userDisplayName }) => {
  return (
    <Box component='header'>
      {_.isEmpty(branding) ? (
        <NormalHeader branding={branding} userDisplayName={userDisplayName} />
      ) : (
        <BrandedHeader branding={branding} userDisplayName={userDisplayName} />
      )}
    </Box>
  );
});

Header.propTypes = {
  branding: PropTypes.object,
  userDisplayName: PropTypes.string,
};

export default Header;
